import { Grid, Line, OrbitControls, Text } from "@react-three/drei";
import type { ThreeEvent } from "@react-three/fiber";
import { Vector3 } from "three";
import { getSceneGridConfig, snapToGrid } from "../lib/grid-utils";
import { appendBlock, updateBlock } from "../state/graph-actions";
import { useBlock, useCurrentSceneId } from "../state/graph-hooks";
import type { NodePosition, SpatialTransform } from "../state/graph-types";
import { useInteractionStore } from "../state/interaction-store";
import { useToolStore } from "../state/tool-store";
import { useUpdateCursor, useYDoc } from "../state/yjs-provider";
import { EntityRenderer } from "./entity-renderer";
import { HexGrid, HexGridCellHighlight } from "./hex-grid";
import { RemoteCursors } from "./remote-cursors";

function getSceneGridConfigForNode(
  node: {
    properties?: Record<string, unknown>;
  } | null,
) {
  return getSceneGridConfig(
    node?.properties as Record<string, unknown> | undefined,
  );
}

export function Scene() {
  const doc = useYDoc();
  const updateCursor = useUpdateCursor();
  const activeTool = useToolStore((s) => s.activeTool);
  const dragState = useInteractionStore((s) => s.dragState);
  const drawState = useInteractionStore((s) => s.drawState);
  const measureState = useInteractionStore((s) => s.measureState);
  const updateDrag = useInteractionStore((s) => s.updateDrag);
  const endDrag = useInteractionStore((s) => s.endDrag);
  const startDraw = useInteractionStore((s) => s.startDraw);
  const updateDraw = useInteractionStore((s) => s.updateDraw);
  const endDraw = useInteractionStore((s) => s.endDraw);
  const startMeasure = useInteractionStore((s) => s.startMeasure);
  const updateMeasure = useInteractionStore((s) => s.updateMeasure);
  const endMeasure = useInteractionStore((s) => s.endMeasure);
  const clearSelection = useInteractionStore((s) => s.clearSelection);

  const sceneId = useCurrentSceneId();
  const sceneNode = useBlock<{ properties?: Record<string, unknown> }>(sceneId);
  const {
    gridSize,
    gridType,
    snapToGrid: isSnapToGridEnabled,
  } = getSceneGridConfigForNode(sceneNode);

  const isDragging =
    dragState !== null || drawState !== null || measureState !== null;

  const pointToGround = (e: ThreeEvent<PointerEvent>): NodePosition => ({
    x: e.point.x,
    y: 0,
    z: e.point.z,
  });

  const snapPosition = (position: NodePosition): NodePosition => {
    if (!isSnapToGridEnabled) return position;

    const snapped = snapToGrid(
      new Vector3(position.x, position.y, position.z),
      gridSize,
      gridType,
    );

    return { x: snapped.x, y: snapped.y, z: snapped.z };
  };

  const handlePointerDown = (e: ThreeEvent<PointerEvent>) => {
    const point = pointToGround(e);

    if (activeTool === "draw") {
      startDraw(point);
      return;
    }

    if (activeTool === "measure") {
      startMeasure(point);
      return;
    }

    // Clicked the background in select mode, so clear current selection
    clearSelection();
  };

  const handlePointerMove = (e: ThreeEvent<PointerEvent>) => {
    const point = pointToGround(e);

    // Broadcast cursor position to other users
    updateCursor({ x: point.x, z: point.z });

    if (drawState) {
      updateDraw(point);
      return;
    }

    if (measureState) {
      updateMeasure(point);
      return;
    }

    if (dragState) {
      const deltaX = e.point.x - dragState.startPosition.x;
      const deltaZ = e.point.z - dragState.startPosition.z;
      updateDrag({ x: deltaX, y: 0, z: deltaZ });
    }
  };

  const handlePointerUp = () => {
    if (drawState) {
      const start = isSnapToGridEnabled
        ? snapPosition(drawState.start)
        : drawState.start;
      const current = isSnapToGridEnabled
        ? snapPosition(drawState.current)
        : drawState.current;
      const width = Math.abs(current.x - start.x);
      const depth = Math.abs(current.z - start.z);

      if (width > 0.2 && depth > 0.2) {
        appendBlock(doc, {
          type: "shape",
          name: "Drawn Area",
          content: "Canvas shape",
          metadata: {
            width,
            depth,
          },
          spatialTransform: {
            position: {
              x: (start.x + current.x) / 2,
              y: 0,
              z: (start.z + current.z) / 2,
            },
          },
        });
      }

      endDraw();
      return;
    }

    if (measureState) {
      endMeasure();
      return;
    }

    if (dragState) {
      // Commit drag into block spatial transform (edgeless spatial view projection)
      const nodesMap = doc.getMap("nodes");
      dragState.activeNodeIds.forEach((id) => {
        const nodeMap = nodesMap.get(id) as
          | {
              get: (key: string) => unknown;
            }
          | undefined;
        if (nodeMap) {
          const spatial = nodeMap.get("spatialTransform") as
            | SpatialTransform
            | undefined;
          const legacyPos = nodeMap.get("position") as NodePosition | undefined;

          const basePos = spatial?.position ?? legacyPos;
          if (!basePos) return;

          const movedPosition = {
            x: basePos.x + dragState.delta.x,
            y: basePos.y + dragState.delta.y,
            z: basePos.z + dragState.delta.z,
          };

          const nextPosition = isSnapToGridEnabled
            ? snapPosition(movedPosition)
            : movedPosition;

          updateBlock(doc, id, {
            spatialTransform: {
              ...spatial,
              position: nextPosition,
            },
          });
        }
      });
      endDrag();
      document.body.style.cursor = "auto";
    }
  };

  const activeSnapPosition = () => {
    if (!isSnapToGridEnabled) return null;
    if (dragState) {
      return snapPosition({
        x: dragState.startPosition.x + dragState.delta.x,
        y: dragState.startPosition.y + dragState.delta.y,
        z: dragState.startPosition.z + dragState.delta.z,
      });
    }
    if (drawState) {
      return snapPosition(drawState.current);
    }
    return null;
  };

  const drawStart = drawState
    ? isSnapToGridEnabled
      ? snapPosition(drawState.start)
      : drawState.start
    : null;
  const drawCurrent = drawState
    ? isSnapToGridEnabled
      ? snapPosition(drawState.current)
      : drawState.current
    : null;

  const drawWidth =
    drawStart && drawCurrent ? Math.abs(drawCurrent.x - drawStart.x) : 0;
  const drawDepth =
    drawStart && drawCurrent ? Math.abs(drawCurrent.z - drawStart.z) : 0;
  const drawCenter = drawState
    ? drawStart && drawCurrent
      ? [
          (drawStart.x + drawCurrent.x) / 2,
          0.03,
          (drawStart.z + drawCurrent.z) / 2,
        ]
      : null
    : null;

  const measureDistance = measureState
    ? Math.hypot(
        measureState.current.x - measureState.start.x,
        measureState.current.z - measureState.start.z,
      )
    : 0;

  const measureMidpoint = measureState
    ? [
        (measureState.start.x + measureState.current.x) / 2,
        0.12,
        (measureState.start.z + measureState.current.z) / 2,
      ]
    : null;

  const snappedActiveCell = activeSnapPosition();

  return (
    <>
      <ambientLight intensity={0.6} />
      <directionalLight position={[10, 20, 10]} intensity={0.4} />

      {/* Background interaction plane */}
      <mesh
        rotation={[-Math.PI / 2, 0, 0]}
        position={[0, -0.01, 0]}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        <planeGeometry args={[1000, 1000]} />
        <meshBasicMaterial visible={false} />
      </mesh>

      {gridType === "hex" ? (
        <HexGrid cellSize={gridSize} fadeDistance={80} />
      ) : (
        <Grid
          args={[100, 100]}
          cellSize={gridSize}
          cellThickness={0.5}
          cellColor="#333333"
          sectionSize={Math.max(1, gridSize * 5)}
          sectionThickness={1}
          sectionColor="#555555"
          fadeDistance={80}
          fadeStrength={1}
          followCamera
          infiniteGrid
        />
      )}

      {snappedActiveCell &&
        (gridType === "hex" ? (
          <HexGridCellHighlight
            position={[snappedActiveCell.x, 0.01, snappedActiveCell.z]}
            cellSize={gridSize}
          />
        ) : (
          <mesh
            position={[snappedActiveCell.x, 0.01, snappedActiveCell.z]}
            rotation={[-Math.PI / 2, 0, 0]}
          >
            <planeGeometry args={[gridSize, gridSize]} />
            <meshBasicMaterial color="#22c55e" transparent opacity={0.2} />
          </mesh>
        ))}

      <EntityRenderer />

      {/* Remote user cursors */}
      <RemoteCursors />

      {drawState && drawCenter && (
        <mesh
          position={drawCenter as [number, number, number]}
          rotation={[-Math.PI / 2, 0, 0]}
        >
          <planeGeometry
            args={[Math.max(drawWidth, 0.01), Math.max(drawDepth, 0.01)]}
          />
          <meshStandardMaterial color="#38bdf8" transparent opacity={0.35} />
        </mesh>
      )}

      {measureState && (
        <>
          <Line
            points={[
              [measureState.start.x, 0.05, measureState.start.z],
              [measureState.current.x, 0.05, measureState.current.z],
            ]}
            color="#facc15"
            lineWidth={2}
          />
          {measureMidpoint && (
            <Text
              position={measureMidpoint as [number, number, number]}
              fontSize={0.22}
              color="#fde68a"
              anchorX="center"
              anchorY="middle"
            >
              {measureDistance.toFixed(1)}u
            </Text>
          )}
        </>
      )}

      <OrbitControls
        makeDefault
        enabled={!isDragging}
        enableRotate={false}
        enablePan
        enableZoom
        minDistance={5}
        maxDistance={100}
        mouseButtons={{
          LEFT: 2, // pan on left drag
          MIDDLE: 1, // dolly on middle
          RIGHT: 2, // pan on right drag
        }}
      />
    </>
  );
}
